// OnboardingGuaranteeScreen — standalone 15-day money-back guarantee
// screen shown as a dedicated onboarding step BEFORE the discount
// promo (`OnboardingAndroidPromoScreen`). Owner's requested sequence:
//
//   Guarantee  →  Code (promo)  →  Plans (/checkout)
//
// Platforms covered:
//   • Android native (Stripe web checkout path)
//   • iOS native in GCC countries routed to Stripe (same path)
//
// iOS users outside GCC still see `OnboardingTrialPromiseScreen` (the
// 3-day free trial pitch) because Apple IAP is their checkout gateway
// and the N10 Stripe coupon doesn't apply to them.
//
// Visual language mirrors the sister promo screens so the transitions
// read as one branded chapter instead of three disconnected cards.

import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Shield } from "lucide-react";

const COPY = {
  ar: {
    kicker: "ضمان كامل 🛡️",
    headline: "جرّب كالوفاي ١٥ يوم بدون مخاطرة",
    sub: "لو ما ناسبك التطبيق لأي سبب — راح نرجّعلك فلوسك كاملة. بدون أسئلة.",
    cta: "متابعة",
  },
  en: {
    kicker: "Full guarantee 🛡️",
    headline: "Try Calofay for 15 days — zero risk",
    sub: "If it's not the right fit for any reason, we'll refund you in full. No questions asked.",
    cta: "Continue",
  },
};

export default function OnboardingGuaranteeScreen({ lang = "ar", onContinue }) {
  const isRTL = lang === "ar";
  const t = COPY[lang] || COPY.ar;

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="always-light fixed inset-0 z-[90] bg-white flex flex-col overflow-hidden"
      data-testid="onboarding-guarantee"
    >
      {/* Calm emerald wash — different accent from the warm amber promo
          so the two consecutive screens feel like distinct chapters. */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(70% 50% at 50% 15%, rgba(167,243,208,0.55) 0%, rgba(255,255,255,0) 65%), linear-gradient(180deg, #ECFDF5 0%, #FFFFFF 55%, #FFFFFF 100%)",
        }}
      />

      <style>{`
        @keyframes guaranteeFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-5px); }
        }
      `}</style>

      <div
        className="relative z-10 flex-1 overflow-y-auto"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 24px)" }}
      >
        <div className="max-w-md mx-auto px-6 pb-40">
          {/* Large shield disc */}
          <motion.div
            initial={{ scale: 0.65, opacity: 0, rotate: -6 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 14, stiffness: 200 }}
            className="flex justify-center mt-2"
          >
            <div
              className="relative w-40 h-40 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(145deg, #A7F3D0 0%, #10B981 55%, #047857 100%)",
                boxShadow:
                  "0 22px 48px -12px rgba(4,120,87,0.45), inset 0 -6px 12px rgba(0,0,0,0.12), inset 0 6px 12px rgba(255,255,255,0.5)",
                animation: "guaranteeFloat 4s ease-in-out infinite",
              }}
            >
              <Shield className="w-20 h-20 text-white drop-shadow-lg" strokeWidth={2.2} />
            </div>
          </motion.div>

          {/* Kicker pill */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="flex justify-center mt-4"
          >
            <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-sm">
              {t.kicker}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.4 }}
            className="text-2xl sm:text-3xl font-black text-neutral-900 text-center leading-tight mt-3 px-2"
          >
            {t.headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.4 }}
            className="text-sm text-neutral-600 text-center mt-3 leading-relaxed px-3"
          >
            {t.sub}
          </motion.p>

          {/* Bullets removed per owner Feb 2026 — keep the screen
              focused on the single guarantee promise. The sub-headline
              above already communicates the full-refund terms. */}

          {/* "ضمان مكتوب وموثّق..." footer line also removed per owner
              Feb 2026 to reduce visual clutter below the headline. */}
        </div>
      </div>

      {/* Sticky CTA */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 pointer-events-none"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)",
          paddingTop: "26px",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 40%, rgba(255,255,255,1) 100%)",
        }}
      >
        <div className="max-w-md mx-auto px-5 pointer-events-auto">
          <button
            type="button"
            onClick={onContinue}
            className="w-full orange-gradient text-white font-black py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2"
            data-testid="onboarding-guarantee-continue"
          >
            {t.cta}
            {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
